import {
  ADMIN_MODE_MIN_SCHEMA_VERSION,
  ATTRIBUTION_MIN_SCHEMA_VERSION,
  FACTOR_ANALYTICS_MIN_SCHEMA_VERSION,
  RISK_MIN_SCHEMA_VERSION,
} from '@/constants/common.constant'
import { statusService } from '@/services/status.service'
import { useEffect, useState } from 'react'

// Parses a dot-separated schema version string (e.g. "17.3.42") into a tuple
// of integers. Returns null if the version is missing, "unknown", or any
// segment is unparsable, so callers can fail closed rather than guessing.
export const parseSchemaVersion = (
  version: string | undefined | null,
): number[] | null => {
  if (!version) return null
  if (version.toLowerCase() === 'unknown') return null
  const segments = version.split('.').map((segment) => parseInt(segment, 10))
  if (segments.some((segment) => Number.isNaN(segment))) return null
  return segments
}

// Lexicographic tuple comparison — e.g. [17, 4, 0] is "at least" [17, 3, 42]
// because 4 > 3, regardless of the trailing 0. Comparing only the last
// segment would treat a routine minor/major bump as a regression the moment
// that segment resets, even though the version is strictly newer.
export const isVersionAtLeast = (
  version: readonly number[],
  minVersion: readonly number[],
): boolean => {
  const length = Math.max(version.length, minVersion.length)
  for (let i = 0; i < length; i++) {
    const actual = version[i] ?? 0
    const required = minVersion[i] ?? 0
    if (actual !== required) return actual > required
  }
  return true
}

export interface SchemaVersionGate {
  // True once the schema-version check has resolved (success or failure) —
  // callers should treat every capability below as not-yet-known (typically
  // rendering their existing loading state) until this is true.
  loaded: boolean
  isFactorAnalyticsEnabled: boolean
  isRiskEnabled: boolean
  isAttributionEnabled: boolean
  isAdminModeEnabled: boolean
}

const DISABLED_CAPABILITIES = {
  isFactorAnalyticsEnabled: false,
  isRiskEnabled: false,
  isAttributionEnabled: false,
  isAdminModeEnabled: false,
}

// Fetches the backend's tracked DB schema version once and derives which
// Factor Analytics capabilities it unlocks. Fails closed (every capability
// false) if the version can't be fetched or parsed — this is a feature gate,
// and showing something that isn't ready yet is worse than hiding something
// that is.
export const useSchemaVersionGate = (): SchemaVersionGate => {
  const [gate, setGate] = useState<SchemaVersionGate>({
    loaded: false,
    ...DISABLED_CAPABILITIES,
  })

  useEffect(() => {
    let cancelled = false

    statusService
      .getSchemaVersion()
      .then(({ version }) => {
        if (cancelled) return
        const parsedVersion = parseSchemaVersion(version)
        setGate({
          loaded: true,
          isFactorAnalyticsEnabled:
            parsedVersion !== null &&
            isVersionAtLeast(parsedVersion, FACTOR_ANALYTICS_MIN_SCHEMA_VERSION),
          isRiskEnabled:
            parsedVersion !== null &&
            isVersionAtLeast(parsedVersion, RISK_MIN_SCHEMA_VERSION),
          isAttributionEnabled:
            parsedVersion !== null &&
            isVersionAtLeast(parsedVersion, ATTRIBUTION_MIN_SCHEMA_VERSION),
          isAdminModeEnabled:
            parsedVersion !== null &&
            isVersionAtLeast(parsedVersion, ADMIN_MODE_MIN_SCHEMA_VERSION),
        })
      })
      .catch((error) => {
        console.error('Failed to fetch schema version:', error)
        if (cancelled) return
        setGate({ loaded: true, ...DISABLED_CAPABILITIES })
      })

    return () => {
      cancelled = true
    }
  }, [])

  return gate
}
